import React from 'react';
import { useTranslation } from 'react-i18next';
import { getTextForVisibility } from '../../utils/utils';
import './VisibilityTag.scss';

export interface VisibilityTagProps {
  /** Visibility percentage (0–100). The qualitative level + stripe colour are derived from it. */
  percent: number;
}

// Maps each qualitative visibility level (from `getTextForVisibility`) to a BEM modifier that
// colours the stripe. The scale runs low → high: grey → red → orange → amber → blue → green.
const VISIBILITY_CLASS_MAP: Record<string, string> = {
  notVisible: 'visibility-tag--not-visible',
  minimal: 'visibility-tag--minimal',
  emerging: 'visibility-tag--emerging',
  decent: 'visibility-tag--decent',
  respectable: 'visibility-tag--respectable',
  good: 'visibility-tag--good',
  excellent: 'visibility-tag--excellent',
};

/**
 * Compact badge for a brand-visibility level. Reuses the global `tag tag--stats tag--mention`
 * pill (white bg + bordered + coloured left stripe) — same look as `MetadataBadge`/`TrendTag` —
 * with the stripe colour encoding the visibility level. Used in the ProjectsPage Visibility column.
 */
export const VisibilityTag: React.FC<VisibilityTagProps> = ({ percent }) => {
  const { t } = useTranslation();
  const levelKey = getTextForVisibility(percent);
  const modifier = VISIBILITY_CLASS_MAP[levelKey] ?? VISIBILITY_CLASS_MAP.notVisible;

  return (
    <span className={`tag tag--stats tag--mention visibility-tag ${modifier}`}>
      <span className="tag__stripe" />
      <span className="tag__value">{t(levelKey)}</span>
    </span>
  );
};

export default VisibilityTag;
